import {put, select, takeEvery} from 'redux-saga/effects'
import {
    ARRIVAL_AIRPORT_SELECTED,
    BAGGAGE_FILTER_SELECTED,
    CLEAR_FILTER,
    FLIGHT_DURATION_CHANGE_UPDATE,
    FLIGHT_DURATIONS_BATCH_RECEIVED,
    FLIGHTS_IMPORT_END,
    FORWARD_TIME_CHANGE_UPDATE,
    FORWARD_TIME_TYPE_CHANGE,
    NO_NIGHT_TRANSFER_SELECT,
    PARENT_FILTERS_CHANGED,
    RENDER_NEXT_PAGE,
    RESET_ALL_FILTERS,
    RETURN_AIRPORT_SELECTED,
    RETURN_TIME_CHANGE_UPDATE,
    RETURN_TIME_TYPE_CHANGE,
    SELECT_CARRIER_FILTER,
    SELECT_TRANSFER_TIME_FILTER_UPDATE,
    SOCKET_FILTER_RESPOND,
    SOCKET_FLIGHTS_RECEIVED,
    STOP_AIRPORT_SELECTED,
    STOPS_SELECT,
    toggleOverlay,
    UPDATE_MOBISCROLL
} from "../actions/FlightsActions"
import {CHANGE_SORT} from "../actions/SortActions";
import {updateFilterUrl} from "../actions/SocketActions";
import FilterEncoder from "../helpers/FilterEncoder";
import IframeHelper from "../helpers/IframeHelper";

function* fetchFiltered(action) {
    const state = yield select();
    // The import stream ended without a single fare. Nothing will be emitted below, so no
    // 'flights-results-filter-response' ever comes back — and that response is the only thing
    // that dispatches toggleOverlay(false). Raising the overlay here would strand it on
    // forever, leaving the spinner running on top of the "no results" message (SUP-2296).
    const noResults = action.type === FLIGHTS_IMPORT_END && action.data === 'no results';

    if (action.type !== SOCKET_FLIGHTS_RECEIVED
        && !(action.type === PARENT_FILTERS_CHANGED
            && action.firstRequest)
        && !action.noDelay
        && !noResults
    ) {
        yield put(toggleOverlay(true));
    }

    // Whatever raised the overlay earlier in the search can no longer be answered either.
    if (noResults) {
        yield put(toggleOverlay(false));
    }

    //call filtered list if import ended and no results displayed
    if (action.type === FLIGHTS_IMPORT_END && state.FlightsReducer.flights.length !== 0) {
        return;
    }

    const socket = state.SocketReducer.socket;
    const cacheId = state.SocketReducer.cacheId;

    const nonFilterKeys = ['socketId', 'cacheId', 'sort', 'page'];

    // socket must be connected before calling for results
    // on connect event socket will make initial call
    if (!socket) {
        return;
    }

    const filters = {
        sort: state.SortReducer.field,
        socketId: socket.id,
        cacheId: cacheId,
        page: state.FlightsReducer.loadedPage,
        carriers: state.AirlinesFilterReducer.selected,
        transfer: parseInt(state.TransferTimeFilterReducer.selected, 10),
        duration: {
            start: parseInt(state.FlightDurationFilterReducer.startSelected, 10),
            end: parseInt(state.FlightDurationFilterReducer.endSelected, 10)
        },
        forwardTime: {
            type: state.ForwardTimeFilterReducer.type,
            start: parseInt(state.ForwardTimeFilterReducer.start, 10),
            end: parseInt(state.ForwardTimeFilterReducer.end, 10)
        },
        returnTime: {
            type: state.ReturnTimeFilterReducer.type,
            start: parseInt(state.ReturnTimeFilterReducer.start, 10),
            end: parseInt(state.ReturnTimeFilterReducer.end, 10)
        },
        stops: parseInt(state.StopsFilterReducer.stops, 10),
        noNightTransfer: state.StopsFilterReducer.noNightTransfer,
        arrivalAirports: state.ArrivalAirportsFilterReducer.selected,
        returnAirports: state.ReturnAirportsFilterReducer.selected,
        stopAirports: state.StopAirportsFilterReducer.selected,
        baggage: state.BaggageFilterReducer.selected
    };

    const filtersArray = Object.keys(filters).map((filter) => {
        return {name: filter, filter: filters[filter]};
    });

    for(let i = 0; i < filtersArray.length; i++) {
        if (nonFilterKeys.indexOf(filtersArray[i].name) !== -1) {
            continue;
        }

        const filter = {};
        filter[filtersArray[i].name] = filtersArray[i].filter;
        yield put(updateFilterUrl(filter));
    }

    action.type !== PARENT_FILTERS_CHANGED && rebuildUrl(state, filters);

    if (!noResults && !(action.type === PARENT_FILTERS_CHANGED && action.firstRequest)) {
        socket.emit('flights-results-filter', filters);
    }
}

function rebuildUrl(state, filters) {
    const parameters = state.ParametersReducer.parameters;
    parameters['projectUrl'] && parameters['search-params'] && filters
    && IframeHelper.updateHeight(parameters['projectUrl'], IframeHelper.getUrlWithFilters(parameters['url'], FilterEncoder.encode(parameters['search-params'], filters)));
}

function updateDuration() {
    if (window.mobiscroll) {
        window.mobiscroll.form('.mobiscroll-refresh-element') &&
        typeof window.mobiscroll.form('.mobiscroll-refresh-element').refresh === 'function' &&
        window.mobiscroll.form('.mobiscroll-refresh-element').refresh();
    }
}

function refreshMobiscrollForm() {
    window.mobiscroll && window.mobiscroll.form('.mobiscroll-refresh-element');
}

function* filteredOverlayRemove() {
    yield put(toggleOverlay(false));
}

function* sagas() {
    //initiate filtering sagas
    yield takeEvery(SELECT_CARRIER_FILTER, fetchFiltered);
    yield takeEvery(SELECT_TRANSFER_TIME_FILTER_UPDATE, fetchFiltered);
    yield takeEvery(FLIGHT_DURATION_CHANGE_UPDATE, fetchFiltered);
    yield takeEvery(FORWARD_TIME_CHANGE_UPDATE, fetchFiltered);
    yield takeEvery(FORWARD_TIME_TYPE_CHANGE, fetchFiltered);
    yield takeEvery(RETURN_TIME_CHANGE_UPDATE, fetchFiltered);
    yield takeEvery(RETURN_TIME_TYPE_CHANGE, fetchFiltered);
    yield takeEvery(STOPS_SELECT, fetchFiltered);
    yield takeEvery(NO_NIGHT_TRANSFER_SELECT, fetchFiltered);
    yield takeEvery(ARRIVAL_AIRPORT_SELECTED, fetchFiltered);
    yield takeEvery(RETURN_AIRPORT_SELECTED, fetchFiltered);
    yield takeEvery(STOP_AIRPORT_SELECTED, fetchFiltered);
    yield takeEvery(BAGGAGE_FILTER_SELECTED, fetchFiltered);
    yield takeEvery(RENDER_NEXT_PAGE, fetchFiltered);
    yield takeEvery(CHANGE_SORT, fetchFiltered);
    yield takeEvery(PARENT_FILTERS_CHANGED, fetchFiltered);
    yield takeEvery(CLEAR_FILTER, fetchFiltered);
    yield takeEvery(RESET_ALL_FILTERS, fetchFiltered);

    yield takeEvery(FLIGHT_DURATIONS_BATCH_RECEIVED, updateDuration);
    yield takeEvery(UPDATE_MOBISCROLL, updateDuration);
    yield takeEvery(SOCKET_FILTER_RESPOND, refreshMobiscrollForm);

    yield takeEvery(SOCKET_FLIGHTS_RECEIVED, fetchFiltered);
    yield takeEvery(FLIGHTS_IMPORT_END, fetchFiltered);

    yield takeEvery(SOCKET_FILTER_RESPOND, filteredOverlayRemove);
}

export default sagas;



// WEBPACK FOOTER //
// ./src/saga/filterSaga.js